import { createContext, useCallback, useContext, useState, type ReactNode } from "react"
import { CheckCircle2, X, AlertTriangle, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"

type ToastVariant = "success" | "error" | "warning"

interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastOptions {
  variant?: ToastVariant
  /** ms before auto-dismiss. Defaults to 4000. Pass 0 (or Infinity) for a
   *  persistent toast that only goes away on action / manual dismiss. */
  duration?: number
  /** Renders a single action button on the right of the toast. */
  action?: ToastAction
  /** A toast already on screen with the same id is REPLACED (message, variant,
   *  action, and a fresh timer) instead of a second one stacking up. Without an
   *  id every call adds a new toast, as before. */
  id?: string
}

interface ToastItem {
  id: number
  /** The caller's id (ToastOptions.id), when it gave one. */
  key?: string
  message: string
  variant: ToastVariant
  action?: ToastAction
}

interface ToastContextValue {
  toast: (message: string, variantOrOptions?: ToastVariant | ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const toast = useCallback<ToastContextValue["toast"]>((message, variantOrOptions) => {
    // Backwards-compat: callers can still pass a bare variant string.
    const opts: ToastOptions =
      typeof variantOrOptions === "string"
        ? { variant: variantOrOptions }
        : variantOrOptions ?? {}
    const variant: ToastVariant = opts.variant ?? "success"
    const id = Date.now() + Math.random()
    const item: ToastItem = { id, key: opts.id, message, variant, action: opts.action }
    setItems((prev) => {
      // Replace in place under a NEW internal id: the replaced toast's timer
      // removes by its own internal id, so it can never dismiss this one.
      const at = opts.id === undefined ? -1 : prev.findIndex((t) => t.key === opts.id)
      if (at === -1) return [...prev, item]
      const next = prev.slice()
      next[at] = item
      return next
    })
    const ttl = opts.duration ?? 4000
    // duration 0 / negative / Infinity ⇒ persistent (caller dismisses it).
    if (Number.isFinite(ttl) && ttl > 0) {
      setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id))
      }, ttl)
    }
  }, [])

  function dismiss(id: number) {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {items.map((t) => {
          const tone =
            t.variant === "success"
              ? "border-emerald-500/40 text-emerald-700"
              : t.variant === "warning"
                ? "border-amber-500/60 bg-amber-50 text-amber-900"
                : "border-destructive/50 text-destructive"
          const Icon =
            t.variant === "success"
              ? CheckCircle2
              : t.variant === "warning"
                ? AlertTriangle
                : AlertCircle
          const iconClass =
            t.variant === "success"
              ? "text-emerald-500"
              : t.variant === "warning"
                ? "text-amber-600"
                : "text-destructive"
          return (
            <div
              key={t.id}
              role="status"
              className={cn(
                "animate-slide-up flex items-start gap-2 rounded-md border bg-card px-4 py-3 shadow-lg max-w-sm text-sm",
                tone,
              )}
            >
              <Icon className={cn("h-4 w-4 mt-0.5", iconClass)} />
              <span className="flex-1">{t.message}</span>
              {t.action ? (
                <button
                  type="button"
                  onClick={() => {
                    t.action!.onClick()
                    dismiss(t.id)
                  }}
                  className="text-xs font-medium underline hover:no-underline"
                >
                  {t.action.label}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    return {
      toast: (msg: string) => {
        // No provider mounted — fall back to console so tests aren't crashed.
        // eslint-disable-next-line no-console
        console.info("[toast]", msg)
      },
    }
  }
  return ctx
}
